<template>
  <button
    class="button-icon"
    ref="refRoot"
    :size="size"
    :theme="_theme"
    :hovering="isHovering ? true : null"
    :ariaHidden="ariaHidden ? true : null"
    :tabindex="ariaHidden ? -1 : null"
  >
    <IconArrowForward
      v-if="icon == 'forward'"
      class="icon"
    />
    <IconArrowBack
      v-if="icon == 'back'"
      class="icon"
    />
    <IconArrowDown
      v-if="icon == 'down'"
      class="icon"
    />
    <IconClose
      v-if="icon == 'close'"
      class="icon"
    />
    <IconMenu
      v-if="icon == 'menu'"
      class="icon"
    />
    <IconPlus
      v-if="icon == 'plus'"
      class="icon"
    />
    <IconPlay
      v-if="icon == 'play'"
      class="icon"
    />
    <IconPause
      v-if="icon == 'pause'"
      class="icon"
    />

    <slot v-if="!icon" />
  </button>
</template>

<script setup lang="ts">
import IconArrowForward from "@/icons/arrow-forward-20.svg?component"
import IconArrowBack from "@/icons/arrow-back-20.svg?component"
import IconArrowDown from "@/icons/arrow-down-20.svg?component"
import IconClose from "@/icons/close-20.svg?component"
import IconMenu from "@/icons/menu-20.svg?component"
import IconPlus from "@/icons/plus2-20.svg?component"
import IconPlay from "@/icons/play-20.svg?component"
import IconPause from "@/icons/pause-20.svg?component"

const props = defineProps({
  icon: {},
  size: {
    type: String,
    default: "small", // small (20), large (24)
  },
  theme: {},
  hovering: {},
  ariaHidden: {
    default: false,
  },
})

const emits = defineEmits([])

const { $gsap, $ScrollTrigger } = useNuxtApp()

const { app, debug, browser } = useStores()

const refRoot = ref(null)

// *********************************************
// HOVERING
// *********************************************

const _hovering = useElementHover(refRoot)
const isHovering = computed(() => {
  if (props.hovering != undefined) return props.hovering
  return _hovering.value
})

// *********************************************
// THEME
// *********************************************

const injectedTheme = inject("theme")

const _theme = computed(() => {
  if (props.theme) {
    return props.theme
  }
  if (injectedTheme) {
    return injectedTheme
  }
  return "white"
})
</script>

<style lang="scss" scoped>
@import "@/styles/shared.scss";

.button-icon {
  position: relative;
  aspect-ratio: 1;
  border-radius: 50%;
  backdrop-filter: blur(40px);
  color: $cl-light;
  transition: all 0.2s ease;
}

.icon {
  @include centerImg;
}

// *********************************************
// SIZE
// *********************************************

.button-icon[size="large"] {
  width: 4.8rem;
  min-width: 4.8rem;

  .icon {
    width: 2.4rem;
  }
}

.button-icon[size="small"] {
  width: 3.2rem;
  min-width: 3.2rem;

  .icon {
    width: 2rem;
  }
}

// *********************************************
// THEME
// *********************************************
.button-icon[theme="black"] {
  background: rgba($cl-light, 0.15);

  &[hovering] {
    background: rgba($cl-light, 0.1);
  }

  &[active] {
    background: rgba($cl-light, 0.2);
  }

  &[disabled] {
    background: rgba($cl-light, 0.15);
    opacity: 0.5;
  }
}

.button-icon[theme="white"] {
  background: rgba($cl-black, 0.3);

  &[hovering] {
    background: rgba($cl-black, 0.2);
  }

  &[active] {
    background: rgba($cl-black, 0.35);
  }

  &[disabled] {
    background: rgba($cl-black, 0.3);
    opacity: 0.5;
  }
}

.button-icon[theme="story"] {
  background: rgba($cl-black, 0.1);
  color: $cl-black;

  &[hovering] {
    background: rgba($cl-black, 0.2);
  }
}
</style>
